// ═══════════════════════════════════════════════════════════════════════
// Paramètres › un domaine (Temps et pointage, Congés et absences, Heures supplémentaires) — Lot 1.
// Tableau lisible : Paramètre · Valeur active · Source · Depuis · Statut · Action. La fiche d'une
// règle montre ce qu'elle fait, si elle est réellement appliquée, son historique et ses simulations ;
// préparer une nouvelle version (brouillon), simuler, soumettre ; valider ou refuser (autre personne).
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Eye, Calculator, Send, Ban } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, EtatVide, Squelette, ChampRecherche, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import {
  lireCfg, ecrireCfg, messageErreur, NavParametres, DOMAINE_DU_SLUG, NIVEAUX, SOURCES, CONFIANCES,
  BadgeStatutRegle, BadgeBranchement, ResumeSimulation, FenetreValider, FenetreMotifCfg,
} from './configurationCommun';
import ReglementDispositifPanneau from './ReglementDispositifPanneau';

const UNITES = { MINUTES: 'min', HEURES_SEMAINE: 'h par semaine', HEURES: 'h', JOURS: 'jours', NOMBRE: 'mois' };
const LIBELLES_CHOIX = {
  SEUIL: 'Seuil : au-delà de la tolérance, tout l\'écart compte', FRANCHISE: 'Franchise : la tolérance est retirée de l\'écart', OUI: 'Oui', NON: 'Non',
};
const aujourdHui = () => new Date().toISOString().slice(0, 10);

function useSites() {
  const [sites, setSites] = useState([]);
  useEffect(() => { axios.get('/api/referentiel/sites').then((r) => setSites(r.data.sites || [])).catch(() => setSites([])); }, []);
  return sites;
}

/** Préparer (ou modifier) une version : brouillon, puis simulation et soumission depuis la fiche. */
function FenetrePreparer({ regle, version, onFermer, onTermine }) {
  const sites = useSites();
  const [f, setF] = useState(() => ({
    valeur: version ? version.valeur_saisie ?? '' : '', date_debut: version ? version.date_debut : aujourdHui(), date_fin: version ? version.date_fin || '' : '',
    niveau: version ? version.niveau : regle.niveau_defaut, site_id: version ? version.site_id || '' : '',
    source: version ? version.source : 'DECISION_DIRECTION', reference_juridique: version ? version.reference_juridique || '' : '',
    niveau_confiance: version ? version.niveau_confiance : 'A_VALIDER_DRH', motif: version ? version.motif || '' : '',
  }));
  const [erreur, setErreur] = useState('');
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const enregistrer = async () => {
    try {
      const corps = { ...f, code: regle.code, valeur: ['DUREE', 'NOMBRE'].includes(regle.type) ? Number(f.valeur) : f.valeur,
        site_id: f.site_id || null, date_fin: f.date_fin || null };
      const v = version ? await ecrireCfg('patch', `/versions/${version.id}`, corps) : await ecrireCfg('post', '/versions', corps);
      onTermine(v, version ? 'Brouillon modifié. Simulez-le puis soumettez-le à validation.' : 'Brouillon enregistré. Simulez-le puis soumettez-le à validation.');
    } catch (e) { setErreur(messageErreur(e)); }
  };
  const bornes = regle.min !== null && regle.max !== null && regle.type !== 'HEURE';
  return (
    <Fenetre titre={version ? `Modifier le brouillon — ${regle.libelle}` : `Nouvelle version — ${regle.libelle}`} Icone={Plus} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Champ libelle="Valeur" requis aide={regle.type === 'HEURE' ? 'Heure du jour (heure de Dakar). Une plage de nuit peut traverser minuit.' : bornes ? 'Une valeur hors des limites prévues est refusée.' : undefined}>
        {regle.type === 'LISTE' ? (
          <select className="rh-champ-saisie" value={f.valeur} onChange={maj('valeur')}>
            <option value="">Choisir…</option>
            {(regle.valeurs || []).map((c) => <option key={c} value={c}>{LIBELLES_CHOIX[c] || c}</option>)}
          </select>
        ) : regle.type === 'HEURE' ? (
          <input type="time" className="rh-champ-saisie" value={f.valeur} onChange={maj('valeur')} style={{ maxWidth: 160 }} />
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="0" step={regle.entier ? 1 : 0.5} className="rh-champ-saisie" value={f.valeur} onChange={maj('valeur')} style={{ maxWidth: 160 }} />
            <span>{regle.code === 'CONGE_ACQUISITION_MENSUELLE_JOURS' ? 'jours par mois' : UNITES[regle.unite] || ''}</span>
          </span>
        )}
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_debut} onChange={maj('date_debut')} /></Champ>
        <Champ libelle="Date de fin" aide="Facultative : une version suivante la ferme d'elle-même."><input type="date" className="rh-champ-saisie" value={f.date_fin} onChange={maj('date_fin')} /></Champ>
      </div>
      <Champ libelle="Niveau de la règle" aide="Une règle inférieure ne peut jamais être moins favorable qu'une règle supérieure en vigueur.">
        <select className="rh-champ-saisie" value={f.niveau} onChange={maj('niveau')}>
          {NIVEAUX.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Champ>
      {regle.par_site && f.niveau === 'SOGAS' && (
        <Champ libelle="Portée" aide="Toute l'entreprise, ou un seul site (la valeur du site l'emporte sur celle de l'entreprise).">
          <select className="rh-champ-saisie" value={f.site_id} onChange={maj('site_id')}>
            <option value="">Toute l'entreprise</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </Champ>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Source" requis>
          <select className="rh-champ-saisie" value={f.source} onChange={maj('source')}>
            {Object.entries(SOURCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Niveau de confiance" requis>
          <select className="rh-champ-saisie" value={f.niveau_confiance} onChange={maj('niveau_confiance')}>
            {Object.entries(CONFIANCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
      </div>
      <Champ libelle="Référence (texte, article)"><input className="rh-champ-saisie" value={f.reference_juridique} onChange={maj('reference_juridique')} placeholder="Exemple : Code du travail, art. 249" /></Champ>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Plus} disabled={f.valeur === '' || !f.date_debut || f.motif.trim().length < 3} onClick={enregistrer}>
          {version ? 'Enregistrer le brouillon' : 'Préparer le brouillon'}
        </Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Fiche d'une règle : définition, branchement, historique, simulations et actions. */
function FenetreRegle({ code, onFermer, onChange }) {
  const { donnees, erreur, recharger } = useChargement(() => lireCfg(`/regles/${code}`), [code]);
  const [sous, setSous] = useState(null);
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState('');
  const agir = async (fn, m) => {
    try { setErreurAction(''); await fn(); setMessage(m); await recharger(); onChange(); } catch (e) { setErreurAction(messageErreur(e)); }
  };
  if (!donnees) {
    return <Fenetre titre="Règle" onFermer={onFermer} large>{erreur ? <BandeauInfo ton="danger">{erreur}</BandeauInfo> : <Squelette hauteur={120} />}</Fenetre>;
  }
  const { regle: r, droits, versions } = donnees;
  const enCours = versions.filter((v) => ['BROUILLON', 'A_VALIDER'].includes(v.statut.code));
  const historique = versions.filter((v) => !['BROUILLON', 'A_VALIDER'].includes(v.statut.code));
  return (
    <Fenetre titre={r.libelle} onFermer={onFermer} large>
      {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
      {erreurAction && <BandeauInfo ton="danger">{erreurAction}</BandeauInfo>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <BadgeBranchement branchement={r.branchement} />
        <span style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>{r.branchement.libelle}{r.branchement.luPar ? ` — lue par : ${r.branchement.luPar}` : ''}</span>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>
        {r.par_portee ? <>Réglée <strong>par dispositif</strong> (règlement du dispositif, de la campagne ou du type d'aide) : {r.portees_reglees} portée(s) en vigueur.</> : <>
        Valeur en vigueur : <strong>{r.active ? r.active.valeur : 'non réglée'}</strong>
        {r.active ? ` (${r.active.niveau_libelle.toLowerCase()}, depuis le ${formaterDate(r.active.date_debut)})` : ' — les calculs qui en dépendent ne produisent aucune conséquence.'}
        {r.variantes_site > 0 ? ` · ${r.variantes_site} valeur(s) propre(s) à un site` : ''}</>}
      </p>
      {droits.preparer && !r.par_portee && <Bouton variante="primaire" Icone={Plus} onClick={() => setSous({ type: 'preparer' })}>Préparer une nouvelle version</Bouton>}
      {enCours.length > 0 && <h3 style={{ fontSize: 15, margin: '16px 0 8px' }}>En préparation et à valider</h3>}
      {enCours.map((v) => (
        <Carte key={v.id} titre={`${v.valeur} · à partir du ${formaterDate(v.date_debut)}`} action={<BadgeStatutRegle statut={v.statut} />}>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
            {v.niveau_libelle} · {SOURCES[v.source] || v.source}{v.reference_juridique ? ` (${v.reference_juridique})` : ''} · préparée par {v.auteur || '—'} · motif : {v.motif}
            {v.motif_refus_ou_annulation && v.statut.code === 'BROUILLON' ? ` · renvoyée : ${v.motif_refus_ou_annulation}` : ''}
          </p>
          {v.simulation ? <ResumeSimulation simulation={v.simulation} /> : r.simulation_requise && <ResumeSimulation simulation={null} />}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {v.statut.code === 'BROUILLON' && droits.preparer && !v.pack_id && <>
              <Bouton petit onClick={() => setSous({ type: 'preparer', version: v })}>Modifier</Bouton>
              <Bouton petit Icone={Calculator} onClick={() => agir(() => ecrireCfg('post', `/versions/${v.id}/simuler`), 'Simulation calculée (aucune donnée modifiée).')}>Simuler</Bouton>
              <Bouton petit variante="primaire" Icone={Send} onClick={() => agir(() => ecrireCfg('post', `/versions/${v.id}/soumettre`), 'Soumise à validation. Une autre personne habilitée doit la valider.')}>Soumettre à validation</Bouton>
            </>}
            {v.statut.code === 'A_VALIDER' && droits.simuler && <Bouton petit Icone={Calculator} onClick={() => agir(() => ecrireCfg('post', `/versions/${v.id}/simuler`), 'Simulation recalculée.')}>Relancer la simulation</Bouton>}
            {v.peut_valider && <Bouton petit variante="primaire" onClick={() => setSous({ type: 'valider', version: v })}>Valider</Bouton>}
            {v.statut.code === 'A_VALIDER' && droits.valider && !v.pack_id && <Bouton petit variante="danger" onClick={() => setSous({ type: 'refuser', version: v })}>Refuser</Bouton>}
            {(droits.preparer || droits.valider) && !v.pack_id && <Bouton petit variante="discret" Icone={Ban} onClick={() => setSous({ type: 'annuler', version: v })}>Retirer</Bouton>}
            {v.pack_id && <span style={{ fontSize: 13 }}>Fait partie d'un pack réglementaire : il se soumet et se valide en bloc.</span>}
          </div>
        </Carte>
      ))}
      <h3 style={{ fontSize: 15, margin: '16px 0 8px' }}>Historique</h3>
      {historique.length === 0 ? <p style={{ fontSize: 14 }}>Aucune version validée pour l'instant.</p> : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Valeur</th><th>Période</th><th>Niveau</th><th>Portée</th><th>Statut</th><th>Préparée par</th><th>Validée par</th></tr></thead>
            <tbody>
              {historique.map((v) => (
                <tr key={v.id}>
                  <td>{v.valeur}</td>
                  <td>{formaterDate(v.date_debut)}{v.date_fin ? ` → ${formaterDate(v.date_fin)}` : ' → …'}</td>
                  <td>{v.niveau_libelle}</td>
                  <td>{v.site_id ? 'Un site' : 'Entreprise'}</td>
                  <td><BadgeStatutRegle statut={v.statut} /></td>
                  <td>{v.auteur || '—'}</td>
                  <td>{v.valideur || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.type !== 'LISTE' && r.type !== 'HEURE' && r.min !== null && (
        <p style={{ marginTop: 10, fontSize: 12, color: 'var(--sg-texte-2)' }}>Limites de saisie : garde-fous techniques. Les minimums légaux viennent des règles de niveau supérieur (loi, convention), datées.</p>
      )}
      {sous?.type === 'preparer' && <FenetrePreparer regle={r} version={sous.version} onFermer={() => setSous(null)}
        onTermine={async (v, m) => { setSous(null); setMessage(m); await recharger(); onChange(); }} />}
      {sous?.type === 'valider' && <FenetreValider titre={`Valider : ${r.libelle} = ${sous.version.valeur}`} resume={<ResumeSimulation simulation={sous.version.simulation} />}
        onFermer={() => setSous(null)} onValider={async (commentaire) => {
          await ecrireCfg('post', `/versions/${sous.version.id}/valider`, { commentaire, confirmation: true });
          setSous(null); setMessage('Règle validée. Elle s\'applique automatiquement à sa date d\'effet.'); await recharger(); onChange();
        }} />}
      {sous?.type === 'refuser' && <FenetreMotifCfg titre="Refuser" aide="La version revient en brouillon à son auteur, avec votre motif." onFermer={() => setSous(null)}
        onConfirmer={async (motif) => { await ecrireCfg('post', `/versions/${sous.version.id}/refuser`, { motif }); setSous(null); setMessage('Version renvoyée en brouillon.'); await recharger(); onChange(); }} />}
      {sous?.type === 'annuler' && <FenetreMotifCfg titre="Retirer cette version" aide="La version est annulée ; elle reste visible dans l'historique." libelleBouton="Retirer" onFermer={() => setSous(null)}
        onConfirmer={async (motif) => { await ecrireCfg('post', `/versions/${sous.version.id}/annuler`, { motif }); setSous(null); setMessage('Version retirée.'); await recharger(); onChange(); }} />}
    </Fenetre>
  );
}

export default function ConfigurationDomainePage() {
  const { domaine: slug } = useParams();
  const domaine = DOMAINE_DU_SLUG[slug];
  const [recherche, setRecherche] = useState('');
  const [ouverte, setOuverte] = useState(null);
  const { donnees, erreur, recharger } = useChargement(() => (domaine ? lireCfg(`/domaines/${domaine}/regles`) : Promise.resolve(null)), [domaine]);
  const regles = useMemo(() => (donnees?.regles || []).filter((r) => !recherche || r.libelle.toLowerCase().includes(recherche.toLowerCase())), [donnees, recherche]);
  if (!domaine) return <Navigate to="/parametres" replace />;
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Paramètres', vers: '/parametres' }, { libelle: donnees?.domaine?.libelle || '…' }]} titre={donnees?.domaine?.libelle || 'Paramètres'}
          sousTitre={donnees?.domaine?.description} />
        <NavParametres actif="" />
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {donnees?.domaine?.par_mandat && (
          <BandeauInfo ton="info">Ces règles se règlent dans le règlement de chaque dispositif (campagne, produit de prêt, type d'aide), par les personnes qui en ont le mandat :
            préparation, simulation, validation par une autre personne. Gabarits : {(donnees.gabarits || []).map((g) => g.libelle).join(' · ')}.</BandeauInfo>
        )}
        {domaine === 'DISPOSITIFS' && <ReglementDispositifPanneau />}
        {domaine === 'DISPOSITIFS' && donnees && <h2 style={{ fontSize: 16, margin: '8px 0 8px' }}>Catalogue des règles des dispositifs</h2>}
        {(donnees?.domaine?.ecrans || []).length > 0 && (
          <p style={{ fontSize: 14, margin: '0 0 12px' }}>Aussi dans ce domaine : {donnees.domaine.ecrans.map((e, i) => <span key={e.lien}>{i ? ' · ' : ''}<a href={e.lien}>{e.libelle}</a></span>)}</p>
        )}
        <div style={{ maxWidth: 360, marginBottom: 12 }}><ChampRecherche valeur={recherche} onChange={setRecherche} placeholder="Rechercher une règle" /></div>
        {!donnees && !erreur && <Squelette hauteur={160} />}
        {donnees && regles.length === 0 && <EtatVide titre="Aucune règle" texte="Aucune règle ne correspond à cette recherche." />}
        {donnees && regles.length > 0 && (
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Paramètre</th><th>Valeur active</th><th>Source</th><th>Depuis</th><th>Statut</th><th aria-label="Action" /></tr></thead>
              <tbody>
                {regles.map((r) => (
                  <tr key={r.code}>
                    <td>{r.libelle} <BadgeBranchement branchement={r.branchement} discret /></td>
                    <td>{r.par_portee ? `Par dispositif (${r.portees_reglees})` : r.active ? r.active.valeur : <span style={{ color: 'var(--sg-texte-2)' }}>Non réglée</span>}{r.variantes_site ? ` (+${r.variantes_site} site)` : ''}</td>
                    <td>{r.active ? SOURCES[r.active.source] || r.active.source : '—'}</td>
                    <td>{r.active ? formaterDate(r.active.date_debut) : '—'}</td>
                    <td>
                      {r.par_portee ? null : r.active ? <BadgeStatutRegle statut={r.active.statut} /> : <BadgeStatutRegle statut={{ code: 'A_VALIDER', libelle: 'Configuration incomplète' }} />}
                      {r.a_valider > 0 && <> <BadgeStatutRegle statut={{ code: 'PROGRAMMEE', libelle: `${r.a_valider} à valider` }} /></>}
                      {r.programmees.length > 0 && <> <BadgeStatutRegle statut={{ code: 'PROGRAMMEE', libelle: `Nouvelle valeur le ${formaterDate(r.programmees[r.programmees.length - 1].date_debut)}` }} /></>}
                    </td>
                    <td><Bouton petit Icone={Eye} onClick={() => setOuverte(r.code)}>Voir</Bouton></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {ouverte && <FenetreRegle code={ouverte} onFermer={() => setOuverte(null)} onChange={recharger} />}
      </div>
    </Layout>
  );
}
